import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import miniInHandImage from '../../../assets/images/mini-in-hand.jpg';
import Loading from '../../../components/Loading';
import { trackNanoCheckoutAddressEdited, trackNanoCheckoutViewed } from '../../../lib/analytics/events';
import { isAddressComplete, ShippingAddress, shippingAddressFromContactInfo } from '../../../lib/checkout/address';
import { appendPetParams, getShopURL } from '../../../lib/shopURL';
import StripeElementsProvider from '../../../lib/stripe/StripeElementsProvider';
import { isStripeConfigured } from '../../../lib/stripe/stripeConfig';
import LinkEmailCard from './LinkEmailCard';
import PaymentSection from './PaymentSection';
import { useBillingPrefill } from '../../../lib/checkout/useBillingPrefill';
import { useCheckoutOffer } from '../../../lib/checkout/useCheckoutOffer';
import { Links } from '../../../lib/constants';
import { petPronouns } from '../../../lib/petUtils';
import { CircledCheck } from '../CheckMark';
import OrderSummary from '../OrderSummary/OrderSummary';
import { useRegisteredChip } from '../useRegisteredChip';
import { useScrollToStepTop } from '../useScrollToStepTop';
import styles from './CheckoutStep.module.scss';
import ShipToCard from './ShipToCard';

/**
 * The in-site checkout, which is now also the bag.
 *
 * The bag used to sit between the offer screen and this one. It was carrying nothing this screen
 * lacked -- the same product row and the same `OrderSummary` component -- while asking the shopper to
 * confirm a decision they had already made one screen earlier, and 70% of the sessions that reached it
 * tapped "Not now" (57 of 82 in the first day at 100%). So its two useful parts moved here: the "what
 * am I getting" list, for a reader to whom "Fi Mini" means nothing, and the headline that says the
 * chip they just registered cannot do this part.
 *
 * What did NOT come with it is the safe-zone map. On a standalone bag it earned a card by replacing a
 * line of copy; on a screen that also has to hold an email card, an address card and a Payment Element
 * it costs height the pay button needs.
 *
 * `BagStep` stays in the tree as the no-offer shop handoff, which is where the redirect below goes.
 *
 * Owns three states: the form, the post-purchase confirmation, and a handoff to the shop for the cases
 * nano cannot finish itself (today that is a 3D Secure challenge, which needs recurly.js).
 */
export default function CheckoutStep() {
  const { loading, petInfo, gwaOffer, contactInfo } = useRegisteredChip();
  const history = useHistory();
  const { resolved, offer, breakdown } = useCheckoutOffer(gwaOffer?.shopUrl ?? null, !loading && !!petInfo);
  const { loading: prefillLoading, accountEmail, billingAddress, cardOnFile } = useBillingPrefill();

  const [address, setAddress] = useState<ShippingAddress | null>(null);
  const [purchased, setPurchased] = useState(false);
  // Only ever set by Stripe's AddressElement. Registration validates none of its address fields, so
  // a locally non-empty address is not evidence the purchase will be accepted.
  const [addressComplete, setAddressComplete] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  // seeded once from what registration collected, falling back to the shopper's Fi account; their
  // own edits own it after that. the email is seeded rather than left blank -- the shopper is always
  // authenticated, so there is always one -- and it lets Link recognise a returning shopper without
  // anyone having to type it.
  useEffect(() => {
    if (loading || prefillLoading) {
      return;
    }
    // seeded even when the chip carried no contact info -- a chip awaiting a transfer has none, and
    // waiting for it left `ready` false forever and the screen spinning. registration's address wins
    // when it captured one (it is fresher); recurly's covers the registrant who skipped those fields
    // but has bought before. an address neither source can complete is a legitimate starting point:
    // ShipToCard prefills whatever it gets and Stripe decides whether it is usable.
    const fromRegistration = shippingAddressFromContactInfo(contactInfo);
    const seeded =
      !isAddressComplete(fromRegistration) && billingAddress && isAddressComplete(billingAddress)
        ? {
            ...billingAddress,
            firstName: billingAddress.firstName || fromRegistration.firstName,
            lastName: billingAddress.lastName || fromRegistration.lastName,
            phone: billingAddress.phone || fromRegistration.phone,
          }
        : fromRegistration;
    setAddress((current) => current ?? seeded);
    setEmail((current) => current ?? contactInfo?.primaryEmail ?? accountEmail);
  }, [loading, prefillLoading, contactInfo, billingAddress, accountEmail]);

  const ready = resolved && !!petInfo && !!address;
  useScrollToStepTop(ready);

  const viewTracked = useRef(false);
  useEffect(() => {
    if (!ready || viewTracked.current || !petInfo || !breakdown) {
      return;
    }
    viewTracked.current = true;
    trackNanoCheckoutViewed({ petId: petInfo.id, gwa: !!gwaOffer });
  }, [ready, petInfo, gwaOffer, breakdown]);

  if (!loading && !petInfo) {
    return <Redirect to={Links.register()} />;
  }

  // waiting on the account prefill too, so the email, address and payment cards render once rather
  // than filling in after they have already been seen
  if (loading || prefillLoading || !ready || !petInfo || !address) {
    return <Loading />;
  }

  // reached directly without a resolvable offer. the bag owns the shop fallback, so send there
  // rather than duplicating that decision here.
  if (!offer || !breakdown) {
    return <Redirect to="/register/bag" />;
  }

  const { possessive, subject, object, usesPluralVerb } = petPronouns(petInfo.gender);
  const capitalisedPossessive = possessive.charAt(0).toUpperCase() + possessive.slice(1);

  // the shop can complete what nano cannot, and it keeps the same offer via the signed GWA url
  const handoffToShop = () => {
    window.location.assign(
      gwaOffer ? appendPetParams(gwaOffer.shopUrl, petInfo) : getShopURL('NanoBag', petInfo, 'mini'),
    );
  };

  // a checkout that cannot take payment is not worth rendering. the email and address cards are both
  // Stripe Elements, so with no key configured they throw "could not find Elements context" and white
  // screen the page rather than degrading. hand off to the shop, which can take the money.
  if (!isStripeConfigured()) {
    handoffToShop();
    return <Loading />;
  }

  if (purchased) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <h1 className={styles.title}>{petInfo.name}’s tracker is on the way.</h1>
          <p className={styles.subtitleConfirmed}>
            We’ve emailed your receipt. Set up {petInfo.name}’s safe zones in the Fi app and the tracker will be ready
            the moment it arrives.
          </p>

          {/* no price breakdown here: they have already paid, and the receipt is the record of what
              they were charged. this screen is about what happens next. */}
          <div className={styles.card}>
            <div className={classNames(styles.productRow, styles.productRowOnly)}>
              <img className={styles.productThumb} src={miniInHandImage} alt="Fi Mini GPS tracker" />
              <div>
                <div className={styles.productName}>Fi Mini GPS tracker</div>
              </div>
            </div>
          </div>

          <div className={styles.ctaContainer}>
            <a className={styles.ctaPrimary} href={Links.iosAppStore}>
              Get the Fi app
            </a>
            <a className={styles.ctaGhost} href={Links.manageHome}>
              Go to my chips
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <StripeElementsProvider>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          {/* back to the offer screen now, not to the bag */}
          <button className={styles.back} type="button" onClick={() => history.goBack()}>
            Back
          </button>

          <div className={styles.eyebrow}>For {petInfo.name}</div>
          <h1 className={styles.title}>Add GPS tracking for {petInfo.name}.</h1>
          {/* "Add", never "GPS tracking for Bella": the second reads as a status, as though the chip
              they just registered already does this. That is the most damaging misunderstanding
              available in this flow, which is why the sub-line below leads with what a chip actually
              does rather than with the payoff -- a bare "always know where she is" here would read as
              a description of what she just registered.
              It also drops the old line's "the safest thing you can do for her". That was an
              unsupportable superlative in a flow that otherwise speaks plainly, and once vs. always is
              the real difference between the two products anyway. */}
          <p className={styles.subtitle}>
            A microchip helps after someone finds {object}. A Fi tracker means you always know where {subject}{' '}
            {usesPluralVerb ? 'are' : 'is'}.
          </p>

          <div className={styles.card}>
            <div className={styles.productRow}>
              <img className={styles.productThumb} src={miniInHandImage} alt="Fi Mini GPS tracker" />
              <div>
                <div className={styles.productName}>Fi Mini GPS tracker</div>
                {/* was "24/7 live GPS tracking for Bella", which the first benefit below now says */}
                <div className={styles.productDetail}>Clips onto {possessive} collar</div>
                <div className={styles.productDetail}>6 months included</div>
              </div>
            </div>

            <OrderSummary breakdown={breakdown} />
          </div>

          {/* the bag's one genuine job: saying what the device does, for a reader who knows Fi only as
              the chip on their yellow card. Still confirming rather than re-selling -- no plan picker,
              no carousel, no alternatives, no clock, and it sits under the arithmetic rather than
              above it. */}
          <div className={styles.card}>
            <div className={styles.sectionTitle}>What {petInfo.name} gets</div>
            <ul className={styles.benefits}>
              <li className={styles.benefit}>
                <CircledCheck />
                {/* a pet with no gender set takes they/them, so the verb has to agree -- the bag
                    shipped this copy without that and reads "see where they is" */}
                <span>
                  See where {subject} {usesPluralVerb ? 'are' : 'is'}, 24/7 live on a map
                </span>
              </li>
              <li className={styles.benefit}>
                <CircledCheck />
                <span>
                  An alert the moment {subject} {usesPluralVerb ? 'leave' : 'leaves'} a safe zone
                </span>
              </li>
              <li className={styles.benefit}>
                <CircledCheck />
                <span>{capitalisedPossessive} steps and sleep, tracked automatically</span>
              </li>
            </ul>
          </div>

          <LinkEmailCard email={email ?? ''} onChange={setEmail} />

          <ShipToCard
            address={address}
            onChange={(next, complete) => {
              setAddress(next);
              setAddressComplete(complete);
            }}
            onCorrected={() => trackNanoCheckoutAddressEdited({ petId: petInfo.id, gwa: !!gwaOffer })}
          />

          <PaymentSection
            email={accountEmail ?? contactInfo?.primaryEmail ?? email ?? ''}
            address={address}
            addressComplete={addressComplete}
            cardOnFile={cardOnFile}
            offer={offer}
            breakdown={breakdown}
            petName={petInfo.name}
            petId={petInfo.id}
            gwa={!!gwaOffer}
            onPurchased={() => setPurchased(true)}
            onFallbackToShop={handoffToShop}
          />
        </div>
      </div>
    </StripeElementsProvider>
  );
}
